import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, default to 'dark'
    const savedTheme = localStorage.getItem('alphabit-theme');
    return savedTheme || 'dark';
  });

  useEffect(() => {
    // Save to localStorage whenever theme changes
    localStorage.setItem('alphabit-theme', theme);
    
    // Apply theme class to document root
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const isDark = theme === 'dark';
  const isLight = theme === 'light';

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark, isLight }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme color constants for consistency
export const themeColors = {
  dark: {
    bg: 'bg-[#0B0F2A]',
    bgSection: 'bg-[#09081A]',
    bgCard: 'bg-[#0F0F2F]',
    bgGradient: 'bg-gradient-to-br from-[#12002F] via-[#2A005F] to-[#0B0F2A]',
    text: 'text-white',
    textMuted: 'text-gray-400',
    textSubtle: 'text-gray-300',
    border: 'border-purple-500/20',
    borderHover: 'hover:border-purple-500/50',
    accent: 'text-[#9D4DFF]',
    accentBg: 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF]',
    cardBg: 'bg-[#0F0F2F]/80 backdrop-blur-xl',
    inputBg: 'bg-white/5',
    inputBorder: 'border-purple-500/30',
    inputFocus: 'focus:border-[#9D4DFF]',
    navBg: 'bg-[#0B0F2A]/90 backdrop-blur-xl',
    navBorder: 'border-purple-500/20',
    shadow: 'shadow-purple-500/20',
    glowShadow: 'shadow-[0_0_40px_rgba(157,77,255,0.5)]',
  },
  light: {
    bg: 'bg-slate-50',
    bgSection: 'bg-white',
    bgCard: 'bg-white',
    bgGradient: 'bg-gradient-to-br from-slate-50 via-white to-slate-100',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    textSubtle: 'text-slate-600',
    border: 'border-slate-200',
    borderHover: 'hover:border-violet-400',
    accent: 'text-violet-600',
    accentBg: 'bg-gradient-to-r from-violet-600 to-purple-600',
    cardBg: 'bg-white shadow-lg',
    inputBg: 'bg-white',
    inputBorder: 'border-slate-300',
    inputFocus: 'focus:border-violet-500',
    navBg: 'bg-white/80 backdrop-blur-xl',
    navBorder: 'border-slate-200/50',
    shadow: 'shadow-slate-200/50',
    glowShadow: 'shadow-xl shadow-violet-500/20',
  }
};

// Helper hook to get current theme colors
export const useThemeColors = () => {
  const { isDark } = useTheme();
  return isDark ? themeColors.dark : themeColors.light;
};
